import React, {useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {readFileAsBase64, readFileAsText} from 'helpers/readFile';
import {AdditionalDocumentParams, DocumentAction} from 'hooks/useSigningDemo';
import {useTranslate} from 'hooks/useTranslate';
import {AgreementTypeSelect} from './AgreementTypeSelect';

interface AdditionalDocumentsProps {
  documents: AdditionalDocumentParams[];
  // Functional updater, so async work (file reads) applies against the latest
  // documents rather than the snapshot captured when it started.
  onChange: (
    update: (
      documents: AdditionalDocumentParams[],
    ) => AdditionalDocumentParams[],
  ) => void;
}

let nextId = 0;
const newId = () => `additional-document-${nextId++}`;

const isPdf = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
const isText = (file: File) =>
  file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');

export const AdditionalDocuments = ({
  documents,
  onChange,
}: AdditionalDocumentsProps) => {
  const t = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();

  const update = (id: string, patch: Partial<AdditionalDocumentParams>) =>
    onChange((prev) => prev.map((d) => (d.id === id ? {...d, ...patch} : d)));

  const addText = () =>
    onChange((prev) => [
      ...prev,
      {
        id: newId(),
        kind: 'TEXT',
        content: '',
        agreementType: '',
        documentAction: 'VIEW',
      },
    ]);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    setError(undefined);
    const added: AdditionalDocumentParams[] = [];
    for (const file of files) {
      try {
        if (isPdf(file)) {
          added.push({
            id: newId(),
            kind: 'PDF',
            fileName: file.name,
            content: await readFileAsBase64(file),
            agreementType: '',
            documentAction: 'VIEW',
          });
        } else if (isText(file)) {
          added.push({
            id: newId(),
            kind: 'TEXT',
            fileName: file.name,
            content: await readFileAsText(file),
            agreementType: '',
            documentAction: 'VIEW',
          });
        } else {
          setError(
            t('demo.signing.documents.error.notPdfOrText', {name: file.name}),
          );
        }
      } catch {
        setError(
          t('demo.signing.documents.error.readFailed', {name: file.name}),
        );
      }
    }
    if (added.length) onChange((prev) => [...prev, ...added]);
  };

  return (
    <Stack spacing={2}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Typography variant="h6" component="h2" sx={{flex: 1}}>
          {t('demo.signing.documents.additional.title')}
        </Typography>
        <Button variant="outlined" onClick={() => inputRef.current?.click()}>
          {t('demo.signing.documents.additional.upload')}
        </Button>
        <Button variant="outlined" onClick={addText}>
          {t('demo.signing.documents.additional.addText')}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept="application/pdf,text/plain,.pdf,.txt"
          data-testid="additional-document-upload"
          onChange={handleFiles}
        />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {documents.map((doc, index) => (
        <Paper key={doc.id} variant="outlined" sx={{p: 2}}>
          <Stack spacing={2}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Typography sx={{flex: 1}}>
                {`${index + 1}. ${doc.kind}${doc.fileName ? `: ${doc.fileName}` : ''}`}
              </Typography>
              <TextField
                select
                size="small"
                label={t('demo.signing.documents.additional.action')}
                value={doc.documentAction}
                onChange={(e) =>
                  update(doc.id, {
                    documentAction: e.target.value as DocumentAction,
                  })
                }
                sx={{minWidth: 120}}>
                <MenuItem value="VIEW">{'VIEW'}</MenuItem>
                <MenuItem value="SIGN">{'SIGN'}</MenuItem>
              </TextField>
              <AgreementTypeSelect
                emptyLabel={t('demo.signing.documents.additional.sameAsMain')}
                value={doc.agreementType}
                onChange={(agreementType) => update(doc.id, {agreementType})}
              />
              <Button
                color="error"
                aria-label={t('demo.signing.documents.additional.removeAria', {
                  number: index + 1,
                })}
                onClick={() =>
                  onChange((prev) => prev.filter((d) => d.id !== doc.id))
                }>
                {t('demo.signing.documents.additional.remove')}
              </Button>
            </Box>
            {doc.kind === 'TEXT' && (
              <TextField
                label={t('demo.signing.documents.additional.text')}
                value={doc.content}
                onChange={(e) => update(doc.id, {content: e.target.value})}
                multiline
                minRows={3}
                fullWidth
              />
            )}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};
